import {
  AllDrum,
  getDrumParentCategory,
  getMelodicParentCategory,
  SampleCategories
} from '@arpeggi/shared'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import ArpRegistry, {
  MAX_DEFAULT_SAMPLE_INDEX
} from '../../../../features/arp-registry/ArpRegistry'
import { AudioEngine } from '../../../audio/engine/AudioEngine'
import { Arp2Utils } from '../../../client/SongUtils'
import { RandIndex } from '../../../generator/RandomUtils'
import { ISampleWithEffectsStore } from './SampleWithEffectsStore'

/** Gets the audio data for a sample */
export function getAudioData(sampleIndex: string) {
  return ArpRegistry.retrieveFromCache(sampleIndex)?.getAudioData()
}

/** Gets the audio data for a stem */
export function getStemAudioData(stemIndex: string) {
  return ArpRegistry.retrieveFromCache(stemIndex)?.getAudioData()
}

/** Loads and caches the audio buffers for multiple samples or stems */
export async function getMultipleSampleAudioBuffers(
  samples: IArpMedia[],
  renderer: 'core' | 'offline' = 'core',
  audioEngine: AudioEngine = null
): Promise<AudioBuffer[]> {
  return await Promise.all(
    samples.map((sample) => sample && sample.fetchAudioBuffer(renderer, audioEngine))
  )
}

export function getRandomSampleForCategory(category: string): ISampleWithEffectsStore {
  const categories: string[] = getNewCategoryListForCategory(category)
  return getRandomSampleFromCategoryList(categories)
}

function getNewCategoryListForCategory(category: string) {
  // Based on the current category, return a new list of all categories that we can now select from.
  if (SampleCategories.Bass.includes(category)) {
    return SampleCategories.Bass
  }
  if (SampleCategories.Kick.includes(category)) {
    return SampleCategories.Kick
  }
  if (SampleCategories.Snare.includes(category)) {
    return SampleCategories.Snare
  }
  if (SampleCategories.Hats.includes(category)) {
    return SampleCategories.Hats
  }
  if (SampleCategories.Percussion.includes(category)) {
    return SampleCategories.Percussion
  }
  if (SampleCategories.Cymbal.includes(category)) {
    return SampleCategories.Cymbal
  }
  return [category]
}

/** Gets a random sample for the given categor(ies). The sample will be from a related category. */
export function getRandomSampleFromCategoryList(categories: string[]): ISampleWithEffectsStore {
  const allSamplesByCategory = groupSamplesByCategory(ArpRegistry.getAllSamples())
  const allSamples: ISampleWithEffectsStore[] = categories
    .flatMap((category) => {
      let samples: IArpMedia[] = allSamplesByCategory[category]
      if (samples === null || samples === undefined || samples.length === 0) {
        const parentCategory = getMelodicParentCategory(category)
        // Melodic Sample
        if (parentCategory) samples = allSamplesByCategory[parentCategory]
        // Drum Sample
        else
          samples = samples =
            allSamplesByCategory['Drum'][category] ||
            allSamplesByCategory['Drum'][getDrumParentCategory(category)[1]]
        if (samples === null || samples.length === 0) {
          return []
        }
      }
      return samples.map((sample) => Arp2Utils.createSampleWithEffects(sample.id, category))
    })
    .filter((sample: ISampleWithEffectsStore) => {
      // For now, only use the original 406 samples.
      // TODO change this to all default samples
      return sample.sampleIndex <= MAX_DEFAULT_SAMPLE_INDEX
    })
  return allSamples[RandIndex(allSamples)]
}

/** Groups all of the samples by their category tag */
export const groupSamplesByCategory = function (samples: IArpMedia[], subcategories = true) {
  const categoryGroupings = {}
  if (subcategories) {
    categoryGroupings['Drum'] = {}
    categoryGroupings['Drum'].name = 'Drums'
  }
  // TODO: samples can be null - this is a bug
  samples
    .filter((a) => a)
    .forEach((sample) => {
      let arr = categoryGroupings
      const category = sample.category
      let parentCategory = getMelodicParentCategory(category) || category

      // Combine open and closed hats
      if (parentCategory === 'Open' || parentCategory === 'Closed') parentCategory = 'Hats'

      // Group drums together as subcategories under "Drums"
      if (subcategories && AllDrum.includes(category)) {
        arr = categoryGroupings['Drum']
      }
      if (!arr[parentCategory]) {
        arr[parentCategory] = []
      }
      arr[parentCategory].push(sample)
    })
  return categoryGroupings
}

/** Groups all of the samples by their artist */
export const groupSamplesByArtist = function (samples: IArpMedia[]) {
  const artistSamples = {}
  // TODO: samples can be null - this is a bug
  samples
    .filter((a) => a)
    .forEach((sample) => {
      if (!artistSamples[sample.user.displayName]) {
        artistSamples[sample.user.displayName] = []
      }
      artistSamples[sample.user.displayName].push(sample)
    })
  const artistGroupings = {}
  Object.keys(artistSamples).forEach((key) => {
    const samples = artistSamples[key]
    // Don't use subcategories if grouping by artist.
    artistGroupings[key] = groupSamplesByCategory(samples as any, false)
  })
  return artistGroupings
}
